'use client';

import { ReactElement } from 'react';
import { AbsoluteFill, Sequence, useCurrentFrame, interpolate } from 'remotion';

// Components
import { ProjectStats } from './components/ProjectStats';
import { DotGridBackground } from '@/components/common/DotGridBackground';
import { ProjectList } from './components/ProjectList';

// Constants
import {
  CLAMP_BOTH,
  SCENE_3B_FADE_IN_DURATION,
  SCENE_3B_TEXT_FADE_OUT_DURATION,
} from '@/constants';
import { DOT_GRID_BACKGROUND_CONFIG } from './constants';

// Types
import { Project } from './components/types';

// Theme
import { employeeYearInReviewTheme } from '../../theme';

// Scene overlap timing - Scene 4 starts 20 frames before Scene 3 ends
const FADE_IN_DURATION = SCENE_3B_FADE_IN_DURATION;

export interface Scene3bProps {
  projects: Project[];
  monthsOnProject?: number;
  /** Duration of this scene in frames - used to calculate text fade out timing */
  sceneDuration?: number;
  /** Frame when the diagonal wipe transition starts (relative to this scene) */
  transitionStartFrame?: number;
}

/**
 * Scene 4: Project highlights and progress
 *
 * This scene showcases project stats and the project list
 * to summarize the employee's year in review.
 */
export const Scene3b = ({
  projects,
  monthsOnProject,
  transitionStartFrame,
}: Scene3bProps): ReactElement => {
  const frame = useCurrentFrame();

  // Background fade in - completes BEFORE Scene 3 starts fading out
  const backgroundOpacity = interpolate(
    frame,
    [0, FADE_IN_DURATION],
    [0, 1],
    CLAMP_BOTH,
  );

  // Text fade out starts before transition begins
  // If transitionStartFrame is provided, calculate when to start fading out text
  const textFadeOutStartFrame = transitionStartFrame
    ? transitionStartFrame - SCENE_3B_TEXT_FADE_OUT_DURATION
    : undefined;

  const { rawColors } = employeeYearInReviewTheme;

  return (
    <AbsoluteFill className={employeeYearInReviewTheme.colors.bgSecondary}>
      {/* DotGridBackground fades in smoothly as Scene 3 background fades out */}
      <Sequence name="Dot Grid Background">
        <AbsoluteFill style={{ opacity: backgroundOpacity }}>
          <DotGridBackground
            {...DOT_GRID_BACKGROUND_CONFIG}
            lineColor={rawColors.gold}
          />
        </AbsoluteFill>
      </Sequence>
      <Sequence name="Project Stats">
        <ProjectStats
          projects={projects}
          monthsOnProject={monthsOnProject}
          textFadeOutStartFrame={textFadeOutStartFrame}
        />
      </Sequence>
      <Sequence name="Project List">
        <ProjectList
          projects={projects}
          textFadeOutStartFrame={textFadeOutStartFrame}
        />
      </Sequence>
    </AbsoluteFill>
  );
};
